import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCompaniesStore } from '@/stores/companies';
import { useEmployeesStore } from '@/stores/employees';

const EXCLUDED_EMPLOYEE_STATUSES = new Set([
  'Resigned',
  'Terminated',
  'Hold',
  'On Hold',
  'Cancelled'
]);

export function useCompanyEmployeeSelection() {
  const companiesStore = useCompaniesStore();
  const employeesStore = useEmployeesStore();
  const { companiesList } = storeToRefs(companiesStore);
  const { employeesList } = storeToRefs(employeesStore);

  const selectedCompanyId = ref(null);
  const selectedEmployeeIds = ref([]);

  const eligibleEmployees = computed(() => employeesList.value.filter(
    employee => !EXCLUDED_EMPLOYEE_STATUSES.has(employee.employee_status)
  ));

  const employeeOptions = computed(() => {
    if (!selectedCompanyId.value) return eligibleEmployees.value;
    return eligibleEmployees.value.filter(
      employee => employee.employee_company_id === selectedCompanyId.value
    );
  });

  const selectedEmployees = computed(() => {
    const selectedIds = new Set(selectedEmployeeIds.value);
    return eligibleEmployees.value.filter(employee => selectedIds.has(employee.employee_id));
  });

  const selectedEmployeeFirstNames = computed(() => selectedEmployees.value.map(employee => (
    employee.employee_name?.trim().split(/\s+/)[0] || `#${employee.employee_id}`
  )));

  const selectionLoading = computed(() => (
    companiesStore.companiesLoading || employeesStore.employeesLoading
  ));

  const clearEmployees = () => {
    selectedEmployeeIds.value = [];
  };

  const handleCompanyChange = () => {
    clearEmployees();
  };

  const setRouteSelection = (companyId, employeeIds) => {
    if (companyId !== null && !companiesStore.companyById(companyId)) {
      return { companyMissing: true, missingEmployeeIds: [] };
    }

    const availableEmployeeIds = new Set(
      eligibleEmployees.value
        .filter(employee => (
          companyId === null || employee.employee_company_id === companyId
        ))
        .map(employee => employee.employee_id)
    );
    const missingEmployeeIds = employeeIds.filter(
      employeeId => !availableEmployeeIds.has(employeeId)
    );
    if (missingEmployeeIds.length) {
      return { companyMissing: false, missingEmployeeIds };
    }

    selectedCompanyId.value = companyId;
    selectedEmployeeIds.value = [...employeeIds];
    return { companyMissing: false, missingEmployeeIds: [] };
  };

  const ensureSelectionData = () => Promise.all([
    companiesStore.ensureCompanies(),
    employeesStore.ensureEmployees()
  ]);

  return {
    companiesList,
    eligibleEmployees,
    employeeOptions,
    selectedCompanyId,
    selectedEmployeeIds,
    selectedEmployees,
    selectedEmployeeFirstNames,
    selectionLoading,
    clearEmployees,
    handleCompanyChange,
    setRouteSelection,
    ensureSelectionData
  };
}
